import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getTasks } from '@/api/dataroom';
import { normalizeError, type NormalizedError } from '@/api/errors';
import type { OverallStatus, Task, TaskStatus } from '@/api/types';
import { OverallBanner } from '@/components/OverallBanner';
import { PageError } from '@/components/PageError';
import { JourneyProgress } from '@/components/JourneyProgress';
import { FinalPage, pageStage, TermsPage, WelcomePage } from '@/components/JourneyPages';
import { RoomShell } from '@/components/RoomShell';
import { TaskCard } from '@/components/TaskCard';
import { useI18n } from '@/i18n/useI18n';
import { useRoomNav } from '@/session/useRoomNav';
import { useSession } from '@/session/useSession';

/**
 * The task flow (F-IS-08). Lists the applicant's tasks with a plain-language "why we ask" on each,
 * and an overall-status banner up top so the applicant always knows where things stand. Task actions
 * report the SERVER-confirmed status back (optimistic-but-honest); a quiet re-fetch keeps the overall
 * banner in sync without disturbing an in-progress card.
 */
export function TasksPage() {
  const { t, dir } = useI18n();
  const nav = useRoomNav();
  const { session } = useSession();
  const journey = session?.journey;
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [overall, setOverall] = useState<OverallStatus | null>(null);
  const [error, setError] = useState<NormalizedError | null>(null);
  // Which authored pages (OL-23) the applicant has moved past. Nothing is recorded: a page is not consent.
  const [passed, setPassed] = useState({ welcome: false, notice: false, final: false });

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getTasks();
      setTasks(res.tasks);
      setOverall(res.overallStatus);
    } catch (err) {
      setError(normalizeError(err));
    }
  }, []);

  // Quiet background sync — keeps the overall banner honest; failures leave current data untouched.
  const refresh = useCallback(async () => {
    try {
      const res = await getTasks();
      setTasks(res.tasks);
      setOverall(res.overallStatus);
    } catch {
      /* keep what we have */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onStatus = useCallback(
    (taskId: string, status: TaskStatus) => {
      setTasks((prev) =>
        prev ? prev.map((tk) => (tk.taskId === taskId ? { ...tk, status } : tk)) : prev,
      );
      void refresh();
    },
    [refresh],
  );

  const stage = tasks ? pageStage(journey?.pages, tasks, passed) : 'tasks';

  let body;
  if (tasks && stage === 'welcome') {
    body = (
      <WelcomePage
        page={journey!.pages!.welcome!}
        tasks={tasks}
        onStart={() => setPassed((p) => ({ ...p, welcome: true }))}
      />
    );
  } else if (tasks && stage === 'terms') {
    body = (
      <TermsPage
        page={journey!.pages!.terms!}
        declaration={tasks[0]}
        onContinue={() => setPassed((p) => ({ ...p, notice: true }))}
        onStatus={onStatus}
        onRefresh={refresh}
      />
    );
  } else if (tasks && stage === 'final') {
    body = (
      <FinalPage
        page={journey!.pages!.final!}
        onBack={() => setPassed((p) => ({ ...p, final: true }))}
        onViewStatus={() => nav('/status')}
      />
    );
  } else if (tasks === null && error) {
    body = <PageError error={error} onRetry={load} />;
  } else if (tasks === null) {
    body = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  } else {
    // The first required step still open: in a sequential journey, nothing after it can start.
    const gate = journey?.sequential
      ? tasks.findIndex((x) => !x.optional && x.status === 'PENDING')
      : -1;
    body = (
      <>
        <div className="dr-intro">
          <h1 className="dr-intro__h">{journey?.heading ?? t('tasks.heading')}</h1>
          <p className="dr-intro__p">
            {journey?.subheading ??
              t(journey?.sequential ? 'tasks.subheadingSequential' : 'tasks.subheading')}
          </p>
        </div>

        {overall && <OverallBanner status={overall} />}

        {tasks.length === 0 ? (
          <Alert type="success" showIcon message={t('tasks.empty')} />
        ) : (
          <>
            <JourneyProgress tasks={tasks} style={journey?.progressStyle ?? 'COUNT'} />

            {tasks.map((tk, i) => (
              <TaskCard
                key={tk.taskId}
                task={tk}
                onStatus={onStatus}
                onRefresh={refresh}
                locked={gate >= 0 && i > gate && tk.status === 'PENDING'}
              />
            ))}

            <Button
              block
              size="large"
              icon={dir === 'rtl' ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => nav('/status')}
              style={{ marginTop: 8 }}
            >
              {t('tasks.viewStatus')}
            </Button>
          </>
        )}
      </>
    );
  }

  return <RoomShell>{body}</RoomShell>;
}
